import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ActionCell } from "@/components/shared/action-cell";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { releaseKeys } from "@/lib/query";
import type { ReleaseStatus } from "@/schemas/releases";

interface Release {
  id: string;
  title: string;
  body: string;
  status: ReleaseStatus;
  releasedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ReleasesListResponse {
  data: Release[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

const statusToneMap: Record<ReleaseStatus, StatusTone> = {
  draft: "neutral",
  released: "success",
};

const LIMIT = 50;

interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

function useAdminReleases(pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...releaseKeys.admin, pageRequest],
    queryFn: async (): Promise<ReleasesListResponse> => {
      const params = new URLSearchParams({ scope: "admin", limit: String(LIMIT) });
      if (pageRequest.cursor) params.set("cursor", pageRequest.cursor);
      params.set("direction", pageRequest.direction);
      const res = await fetch(`/api/releases?${params}`);
      if (!res.ok) throw new Error("Failed to fetch releases");
      return res.json();
    },
  });
}

function useCreateRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string; body: string; status: ReleaseStatus }) => {
      const res = await fetch("/api/releases", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Create failed");
    },
    onSuccess: () => {
      // Prefix invalidation covers both the admin list and the public
      // "What's new" page (releaseKeys.public), which was previously stale.
      qc.invalidateQueries({ queryKey: releaseKeys.all });
      toast.success("Release created");
    },
    onError: () => toast.error("Failed to create release"),
  });
}

function useUpdateRelease(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title?: string; body?: string; status?: ReleaseStatus }) => {
      const res = await fetch(`/api/releases/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Update failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: releaseKeys.all });
      toast.success("Release updated");
    },
    onError: () => toast.error("Failed to update release"),
  });
}

function useDeleteRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/releases/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: releaseKeys.all });
      toast.success("Release deleted");
    },
    onError: () => toast.error("Failed to delete release"),
  });
}

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; release: Release }
  | { type: "delete"; release: Release };

export function SettingsReleasesPage() {
  const [page, setPage] = useState(1);
  const [pageRequest, setPageRequest] = useState<PageRequest>({ cursor: null, direction: "next" });
  const { data, isLoading, isError, error, refetch } = useAdminReleases(pageRequest);
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const deleteRelease = useDeleteRelease();

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load releases"} onRetry={() => refetch()} />
    );
  }

  const releases = data?.data ?? [];

  return (
    <div className="space-y-4">
      <DataToolbar
        actions={
          <Button onClick={() => setDialog({ type: "create" })}>
            <Plus className="mr-2 h-4 w-4" />
            New release
          </Button>
        }
      />

      <DataTable
        headers={[
          { label: "Title" },
          { label: "Status" },
          { label: "Released", className: "hidden md:table-cell" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={<EmptyState icon={Megaphone} title="No releases yet" description="" />}
      >
        {releases.map((r) => (
          <tr
            key={r.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">{r.title}</td>
            <td className="px-4 py-3">
              <StatusPill
                tone={statusToneMap[r.status]}
                label={r.status.charAt(0).toUpperCase() + r.status.slice(1)}
              />
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {r.releasedAt ? new Date(r.releasedAt).toLocaleDateString() : "—"}
            </td>
            <td className="px-4 py-3 text-right">
              <ActionCell
                items={[
                  {
                    icon: Pencil,
                    label: "Edit",
                    onClick: () => setDialog({ type: "edit", release: r }),
                  },
                  {
                    icon: Trash2,
                    label: "Delete",
                    onClick: () => setDialog({ type: "delete", release: r }),
                    variant: "destructive",
                    separatorBefore: true,
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={page}
        limit={LIMIT}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => {
          if (data?.nextCursor) {
            setPage((p) => p + 1);
            setPageRequest({ cursor: data.nextCursor, direction: "next" });
          }
        }}
        onPrev={() => {
          if (data?.prevCursor) {
            setPage((p) => Math.max(1, p - 1));
            setPageRequest({ cursor: data.prevCursor, direction: "prev" });
          }
        }}
        loading={isLoading}
        itemLabel="releases"
      />

      {dialog.type === "create" && (
        <ReleaseDialog open onOpenChange={() => setDialog({ type: "none" })} />
      )}
      {dialog.type === "edit" && (
        <ReleaseDialog
          key={dialog.release.id}
          open
          onOpenChange={() => setDialog({ type: "none" })}
          release={dialog.release}
        />
      )}
      {dialog.type === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          title="Delete release?"
          description={`Permanently deletes ${dialog.release.title}.`}
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            deleteRelease.mutate(dialog.release.id, {
              onSuccess: () => setDialog({ type: "none" }),
            });
          }}
        />
      )}
    </div>
  );
}

function ReleaseDialog({
  open,
  onOpenChange,
  release,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  release?: Release;
}) {
  const isEdit = release != null;
  const create = useCreateRelease();
  const update = useUpdateRelease(release?.id ?? "");
  const [title, setTitle] = useState(release?.title ?? "");
  const [body, setBody] = useState(release?.body ?? "");
  const [status, setStatus] = useState<ReleaseStatus>(release?.status ?? "draft");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    const payload = { title: title.trim(), body: body.trim(), status };
    if (isEdit) await update.mutateAsync(payload);
    else await create.mutateAsync(payload);
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit release" : "New release"}</DialogTitle>
          <DialogDescription>
            Drafts stay private. Switching to "Released" surfaces the entry to all users.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rel-title">Title</Label>
            <Input
              id="rel-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. April 2026 update"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rel-body">Body (markdown OK)</Label>
            <Textarea
              id="rel-body"
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What shipped?"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rel-status">Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as ReleaseStatus)}>
              <SelectTrigger id="rel-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="released">Released</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim() || !body.trim() || pending}>
              {pending ? "Saving…" : isEdit ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
